/**
 * Synapse Upload State Management
 *
 * Jotai atoms for managing Synapse ambient upload flow including:
 * - Ambient session lifecycle (create, complete)
 * - Chunk upload queue coordination with service worker
 * - Session recovery for interrupted recordings
 * - Error handling and retry logic
 *
 * Architecture:
 * - Base atoms: Raw state (sessionId, error, loading)
 * - Derived atoms: Computed values (isSessionActive, canComplete)
 * - Action atoms: State mutations (startSession, completeSession, queueChunk)
 *
 * This centralizes Synapse upload state that was previously scattered in
 * useSynapseAudioUpload.ts with useState/useRef/useEffect patterns.
 */
import { atom } from 'jotai';

import { createDebugLogger } from '@videa/utils';

import {
  trackChunkUploadFailed,
  trackPreprocessingCompleted,
  trackPreprocessingFailed,
  trackPreprocessingStarted,
  trackSessionCompleted,
  trackSessionCompletionFailed,
} from '../analytics/synapseAnalytics';
import { opfs } from '../storage/opfs';
import {
  type AmbientClientDependencies,
  type CompleteAndPreprocessResponse,
  type CreateSessionParams,
  completeAndPreprocess,
  createSession,
  directUploadFromOPFS,
} from '../upload/ambientClient';
import {
  cacheTokenInServiceWorker,
  completeUploadSession,
  isUploadWorkerReachable,
  registerChunkUpload,
  registerUploadSession,
  triggerQueueProcessing,
} from '../upload/backgroundSync';

import { eventTrackerAtom } from './eventTracker';
import {
  finalDurationMsAtom,
  muteSegmentsAtom,
  recordingSessionIdAtom,
} from './recording';

const debug = createDebugLogger('SynapseUpload');

// ============================================================================
// Types
// ============================================================================

export interface SynapseUploadError {
  title: string;
  description: string;
}

export interface SynapseUploadConfig {
  /** Synapse Ambient API URL */
  synapseApiUrl: string;
  /** Current organization ID */
  organizationId: string;
  /** Current practice ID */
  practiceId: string;
}

export interface PendingSessionCreation {
  /** Consent record ID to use when retrying */
  consentRecordId?: string;
  /** Number of retry attempts made */
  retryCount: number;
}

// ============================================================================
// Base Atoms (Source of Truth)
// ============================================================================

/**
 * Current ambient session ID from Synapse/Weaver.
 * null when no session is active.
 */
export const ambientSessionIdAtom = atom<string | null>(null);

/**
 * Synapse upload configuration.
 * Set by the host app when initializing the upload flow.
 */
export const synapseUploadConfigAtom = atom<SynapseUploadConfig | null>(null);

/**
 * Token provider function for Synapse API calls.
 * Set by the host app (returns Okta access token).
 */
export const synapseTokenProviderAtom = atom<(() => Promise<string>) | null>(
  null,
);

/**
 * Loading state for async Synapse operations.
 */
export const synapseUploadLoadingAtom = atom(false);

/**
 * Error state for Synapse upload operations.
 */
export const synapseUploadErrorAtom = atom<SynapseUploadError | null>(null);

/**
 * Pending session creation state for background retry.
 * When session creation fails, we retry in the background while recording continues.
 */
export const pendingSessionCreationAtom = atom<PendingSessionCreation | null>(
  null,
);

/**
 * Timestamp when recording/upload started (for metrics).
 */
export const uploadStartTimeAtom = atom<number>(0);

/**
 * Tracks which OPFS+Ambient session pair has been registered with the SW.
 * Format: "opfsSessionId:ambientSessionId"
 * Prevents duplicate registration.
 */
export const registeredSessionPairAtom = atom<string | null>(null);

/**
 * Tracks which OPFS session ID has session creation in progress.
 * Scoped to OPFS session ID so different recordings can create sessions independently,
 * but the same recording won't create duplicate sessions.
 */
export const sessionCreationInProgressForAtom = atom<string | null>(null);

/**
 * Holds the Promise for the last in-flight chunk write (OPFS + SW registration).
 *
 * With a 30s chunk interval, a recording shorter than 30s only fires
 * `ondataavailable` once—when `recorder.stop()` is called. The async work
 * in that handler (OPFS write + registerChunkUpload) is fire-and-forget from
 * the MediaRecorder's perspective. `completeAmbientSessionAtom` awaits this
 * atom so the chunk is guaranteed to be in OPFS before the completion
 * flow reads and direct-uploads any remaining chunks.
 */
export const pendingChunkWritePromiseAtom = atom<Promise<void>>(
  Promise.resolve(),
);

/**
 * Whether the videa-notes upload service worker is reachable.
 *
 * Checked once at session start via `isUploadWorkerReachable()`.
 * - `null`  = not checked yet
 * - `true`  = our SW is active and responding
 * - `false` = wrong SW (e.g. MSW) or no SW — use direct upload path
 *
 * When `false`, the entire SW communication pipeline (registerUploadSession,
 * registerChunkUpload, cacheToken) is skipped to avoid
 * accumulating timeout delays.
 */
export const uploadWorkerAvailableAtom = atom<boolean | null>(null);

// ============================================================================
// Derived Atoms (Computed from base atoms)
// ============================================================================

/**
 * Whether Synapse upload is properly configured.
 */
export const isSynapseConfiguredAtom = atom((get) => {
  const config = get(synapseUploadConfigAtom);
  const tokenProvider = get(synapseTokenProviderAtom);
  return config !== null && tokenProvider !== null && !!config.synapseApiUrl;
});

/**
 * Whether an ambient session is currently active.
 */
export const isAmbientSessionActiveAtom = atom(
  (get) => get(ambientSessionIdAtom) !== null,
);

/**
 * Whether we're waiting on a background retry for session creation.
 */
export const isSessionCreationPendingAtom = atom(
  (get) => get(pendingSessionCreationAtom) !== null,
);

/**
 * Build dependencies object for ambient API calls.
 */
export const ambientDepsAtom = atom((get): AmbientClientDependencies | null => {
  const config = get(synapseUploadConfigAtom);
  const tokenProvider = get(synapseTokenProviderAtom);

  if (!config || !tokenProvider) {
    return null;
  }

  return {
    getSynapseToken: tokenProvider,
    synapseApiUrl: config.synapseApiUrl,
    organizationId: config.organizationId,
    practiceId: config.practiceId,
  };
});

// ============================================================================
// Action Atoms (State mutations)
// ============================================================================

/**
 * Configure Synapse upload with API URL and auth.
 */
export const configureSynapseUploadAtom = atom(
  null,
  (
    _get,
    set,
    params: {
      config: SynapseUploadConfig;
      tokenProvider: () => Promise<string>;
    },
  ) => {
    debug.log('Configuring Synapse upload', {
      synapseApiUrl: params.config.synapseApiUrl,
      organizationId: params.config.organizationId,
      practiceId: params.config.practiceId,
    });
    set(synapseUploadConfigAtom, params.config);
    set(synapseTokenProviderAtom, () => params.tokenProvider);
  },
);

/**
 * Start an ambient session for recording.
 *
 * @param params.sessionParams - Parameters for creating the session
 * @param params.opfsSessionId - OPFS session ID for SW registration
 * @param params.recoveredAmbientSessionId - Existing session ID if recovering
 * @returns The session ID or null if failed
 */
export const startAmbientSessionAtom = atom(
  null,
  async (
    get,
    set,
    params: {
      sessionParams: CreateSessionParams;
      opfsSessionId?: string;
      recoveredAmbientSessionId?: string;
    },
  ): Promise<string | null> => {
    const deps = get(ambientDepsAtom);
    const config = get(synapseUploadConfigAtom);

    if (!deps || !config) {
      debug.warn('Synapse not configured, cannot start session');
      return null;
    }

    // Race condition guard: prevent duplicate session creation for the same OPFS session
    // Scoped to OPFS session ID so different recordings can proceed independently
    const targetOpfsSessionId =
      params.opfsSessionId ?? get(recordingSessionIdAtom);
    const creationInProgressFor = get(sessionCreationInProgressForAtom);

    if (
      creationInProgressFor &&
      creationInProgressFor === targetOpfsSessionId
    ) {
      debug.log(
        'Session creation already in progress for this OPFS session, waiting...',
        targetOpfsSessionId,
      );
      // Poll for session creation with timeout (session creation can take 500ms-2000ms)
      const MAX_WAIT_MS = 5000;
      const POLL_INTERVAL_MS = 100;
      const startWait = Date.now();

      while (Date.now() - startWait < MAX_WAIT_MS) {
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
        const existingSessionId = get(ambientSessionIdAtom);
        if (existingSessionId) {
          debug.log(
            'Session was created by concurrent call:',
            existingSessionId,
          );
          return existingSessionId;
        }
        // Check if creation is no longer in progress (completed or failed)
        if (get(sessionCreationInProgressForAtom) !== targetOpfsSessionId) {
          break;
        }
      }
      // If we get here, the other call failed or timed out - proceed with our own attempt
      debug.log('Concurrent session creation did not complete, proceeding...');
    }

    set(sessionCreationInProgressForAtom, targetOpfsSessionId);

    debug.log('Starting ambient session', {
      recovered: !!params.recoveredAmbientSessionId,
    });

    set(synapseUploadLoadingAtom, true);
    set(synapseUploadErrorAtom, null);
    set(uploadStartTimeAtom, Date.now());

    try {
      // ── SW reachability check (once per session) ──────────────────
      // Determine up-front whether our upload SW is active.  When it
      // isn't (e.g. MSW registered instead), we skip every SW message
      // to avoid accumulating 2-second timeouts on each postMessage.
      const swAvailable = await isUploadWorkerReachable();
      set(uploadWorkerAvailableAtom, swAvailable);
      if (!swAvailable) {
        debug.warn(
          'Upload service worker not reachable at session start — will use direct upload path',
        );
      }

      // Cache token in service worker for background uploads
      const token = await deps.getSynapseToken();
      if (swAvailable) {
        await cacheTokenInServiceWorker(token);
      }

      let sessionId: string;

      if (params.recoveredAmbientSessionId) {
        // Reuse existing session for recovery
        sessionId = params.recoveredAmbientSessionId;
        debug.log('Using recovered session:', sessionId);
      } else {
        // Create new session
        const session = await createSession(params.sessionParams, deps);
        sessionId = session.id;
        debug.log('Created new session:', sessionId);
      }

      set(ambientSessionIdAtom, sessionId);

      const tracker = get(eventTrackerAtom);
      tracker.setSessionId(sessionId);
      tracker.track('recording_started', {
        recovered: !!params.recoveredAmbientSessionId,
      });

      // Register with service worker (skipped when SW is unavailable)
      const opfsSessionId = params.opfsSessionId ?? get(recordingSessionIdAtom);
      if (opfsSessionId) {
        if (swAvailable) {
          await registerUploadSession(
            opfsSessionId,
            sessionId,
            config.organizationId,
            config.practiceId,
            config.synapseApiUrl,
          );
          set(registeredSessionPairAtom, `${opfsSessionId}:${sessionId}`);
        }

        // Persist to OPFS for recovery (skip if recovering).
        // This is independent of the SW — needed for crash recovery.
        if (!params.recoveredAmbientSessionId) {
          try {
            await opfs.updateAmbientSessionId(opfsSessionId, sessionId);
            // Persist templateId so recovery can restore template selection
            if (params.sessionParams.templateId) {
              await opfs.updateSessionFields(opfsSessionId, {
                templateId: params.sessionParams.templateId,
              });
            }
          } catch (opfsError) {
            debug.warn(
              'Failed to persist session metadata to OPFS:',
              opfsError,
            );
          }
        }

        // Process any queued chunks
        if (swAvailable) {
          await triggerQueueProcessing();
        }
      }

      set(pendingSessionCreationAtom, null);
      set(sessionCreationInProgressForAtom, null);
      set(synapseUploadLoadingAtom, false);

      return sessionId;
    } catch (error) {
      debug.error('Failed to start ambient session:', error);

      // Start background retry instead of failing immediately
      set(pendingSessionCreationAtom, {
        consentRecordId: params.sessionParams.patientConsentRecordId,
        retryCount: 0,
      });

      set(sessionCreationInProgressForAtom, null);
      set(synapseUploadLoadingAtom, false);
      return null;
    }
  },
);

/**
 * Queue a chunk for background upload via service worker.
 * The chunk must already be written to OPFS.
 *
 * When the upload SW is unavailable (e.g. MSW is active), this is a
 * no-op — the chunk stays in OPFS and will be uploaded directly from
 * the main thread during session completion.
 */
export const queueChunkUploadAtom = atom(
  null,
  async (
    get,
    _set,
    params: { chunkIndex: number; opfsSessionId: string },
  ): Promise<void> => {
    const { chunkIndex, opfsSessionId } = params;

    const ambientSessionId = get(ambientSessionIdAtom);
    const deps = get(ambientDepsAtom);

    if (!opfsSessionId) {
      debug.warn(`No opfsSessionId, skipping chunk ${chunkIndex}`);
      return;
    }

    // When the SW is unavailable, skip all SW communication to avoid
    // accumulating 2s timeouts on every chunk.  The chunk is safely
    // in OPFS and will be uploaded directly at completion time.
    if (get(uploadWorkerAvailableAtom) === false) {
      debug.log(
        `Chunk ${chunkIndex} written to OPFS (SW unavailable — will direct-upload at completion)`,
      );
      return;
    }

    try {
      // Get fresh token and pass it directly to the SW with the chunk message.
      // This avoids a race between separate CACHE_TOKEN and UPLOAD_CHUNK messages.
      let token: string | undefined;
      if (deps) {
        token = await deps.getSynapseToken();
        // Also cache separately for background sync retries
        await cacheTokenInServiceWorker(token);
      }

      // Notify service worker to upload the chunk from OPFS.
      // We do not track chunk_upload_success/chunk_upload_failed here: registerChunkUpload
      // only waits for the SW to acknowledge the message; the actual HTTP upload runs
      // asynchronously in the SW. Real success is tracked in completeAmbientSessionAtom
      // via onChunkUploadSuccess when chunks are direct-uploaded from OPFS.
      await registerChunkUpload(opfsSessionId, chunkIndex, token);
      debug.log('Chunk queued for upload:', chunkIndex);
    } catch (error) {
      debug.error('Failed to queue chunk:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      // Do not track chunk_upload_failed here — this catch is for SW registration
      // failure (e.g. timeout, SW not ready), not for actual upload failure.
      const isQuotaError =
        error instanceof Error && error.name === 'QuotaExceededError';
      trackChunkUploadFailed({
        sessionId: opfsSessionId,
        ambientSessionId: ambientSessionId ?? 'unknown',
        chunkIndex,
        error: errorMessage,
        retryCount: 0,
        isQuotaError,
      });
      // Don't throw - chunk is still in OPFS for recovery
    }
  },
);

/** Response from completeAmbientSessionAtom including timing info */
export interface CompleteAmbientSessionResult
  extends CompleteAndPreprocessResponse {
  /** Upload start time for elapsed time calculation */
  startTime: number;
}

/**
 * Complete the ambient session and trigger preprocessing.
 * Returns the nucleus S3 key for the complete-upload call.
 */
export const completeAmbientSessionAtom = atom(
  null,
  async (
    get,
    set,
    params: {
      localTranscript?: string;
    },
  ): Promise<CompleteAmbientSessionResult | null> => {
    // Read ambientSessionId from OPFS to ensure we have the correct one
    // The atom might have stale state if the page was refreshed
    const opfsSessionId = get(recordingSessionIdAtom);
    const atomSessionId = get(ambientSessionIdAtom);
    let ambientSessionId = atomSessionId;

    if (opfsSessionId) {
      try {
        const session = await opfs.readSession(opfsSessionId);
        if (session?.metadata.ambientSessionId) {
          if (session.metadata.ambientSessionId !== atomSessionId) {
            debug.warn(
              `ambientSessionId mismatch: atom=${atomSessionId}, opfs=${session.metadata.ambientSessionId}. Using OPFS value.`,
            );
          }
          ambientSessionId = session.metadata.ambientSessionId;
        }
      } catch (error) {
        debug.warn(
          'Failed to read ambientSessionId from OPFS, using atom value:',
          error,
        );
      }
    }

    const deps = get(ambientDepsAtom);
    const config = get(synapseUploadConfigAtom);
    const startTime = get(uploadStartTimeAtom);

    if (!ambientSessionId) {
      debug.warn('No ambient session to complete');
      set(synapseUploadErrorAtom, {
        title: 'Session Error',
        description: 'No active recording session to complete',
      });
      return null;
    }

    if (!deps || !config) {
      debug.error('Synapse not configured');
      set(synapseUploadErrorAtom, {
        title: 'Configuration Error',
        description: 'Synapse upload is not properly configured',
      });
      return null;
    }

    debug.log(
      'Completing ambient session, using ambientSessionId =',
      ambientSessionId,
    );
    set(synapseUploadLoadingAtom, true);

    // Capture completion start time for accurate s3UploadTime / loadTime metrics.
    // uploadStartTimeAtom tracks when recording began (used for session analytics),
    // but upload timing events should only measure from "Generate Note" click.
    const completionStartTime = Date.now();

    try {
      const swAvailable = get(uploadWorkerAvailableAtom) !== false;

      // Refresh token in service worker before completing
      const token = await deps.getSynapseToken();
      if (swAvailable) {
        await cacheTokenInServiceWorker(token);
      }

      // Wait for the last chunk write to OPFS to finish.
      // With a 30s chunk interval, a short recording's only ondataavailable
      // fires on recorder.stop(). The async OPFS write (+ registerChunkUpload
      // when SW is available) is fire-and-forget from MediaRecorder's
      // perspective, so we must await it here.
      debug.log('Awaiting pending chunk write promise...');
      await get(pendingChunkWritePromiseAtom);
      debug.log('Pending chunk write promise resolved');

      // Read OPFS chunk count for analytics.
      let chunkCount = 0;
      if (opfsSessionId) {
        try {
          const session = await opfs.readSession(opfsSessionId);
          chunkCount = session?.metadata.chunkCount ?? 0;
        } catch {
          // Non-fatal, just for analytics
        }
      }

      // ── Ensure all chunks are confirmed on the server ─────────────
      //
      // Always direct-upload from OPFS when chunks exist.  We cannot
      // trust getPendingChunkCount alone — if registerChunkUpload
      // timed out, the SW was never told about the chunk, so its
      // pending count is 0 even though nothing was uploaded.
      //
      // directUploadFromOPFS handles idempotency:
      //  • Blob present  → upload (harmless duplicate if SW already did it)
      //  • Blob null      → SW uploaded & deleted from OPFS — skip
      if (opfsSessionId && chunkCount > 0) {
        const MAX_UPLOAD_PASSES = 3;
        let lastResult = { uploaded: 0, failed: 0 };

        // Track which chunk indices have already had chunk_upload_success emitted.
        // directUploadFromOPFS does not remove chunks from OPFS, so retry passes
        // re-upload the same chunks; we only want one event per chunk for the timeline.
        const reportedChunkIndices = new Set<number>();
        const tracker = get(eventTrackerAtom);

        for (let pass = 1; pass <= MAX_UPLOAD_PASSES; pass++) {
          debug.log(
            `Upload pass ${pass}/${MAX_UPLOAD_PASSES}: confirming ${chunkCount} chunk(s)`,
          );
          try {
            lastResult = await directUploadFromOPFS(
              opfsSessionId,
              ambientSessionId,
              deps,
              opfs.readSession.bind(opfs),
              undefined, // use default directUploadChunkToWeaver
              (chunkIndex) => {
                if (reportedChunkIndices.has(chunkIndex)) return;
                reportedChunkIndices.add(chunkIndex);
                tracker.track('chunk_upload_success', {
                  chunk_index: chunkIndex,
                });
              },
            );
          } catch (uploadError) {
            // directUploadFromOPFS throws when *every* chunk fails.
            // Treat as a total failure so the retry loop can backoff
            // instead of propagating to the generic catch handler.
            debug.error(
              `Upload pass ${pass} threw:`,
              uploadError instanceof Error
                ? uploadError.message
                : String(uploadError),
            );
            lastResult = { uploaded: 0, failed: chunkCount };
          }
          debug.log(
            `Pass ${pass}: ${lastResult.uploaded} uploaded, ${lastResult.failed} failed`,
          );

          if (lastResult.failed === 0) break;

          if (pass < MAX_UPLOAD_PASSES) {
            const backoffMs = pass * 2000; // 2s, 4s
            debug.warn(
              `${lastResult.failed} chunk(s) failed — retrying in ${backoffMs}ms`,
            );
            await new Promise((r) => setTimeout(r, backoffMs));
          }
        }

        if (lastResult.failed > 0) {
          debug.error(
            `${lastResult.failed} chunk(s) still failing after ${MAX_UPLOAD_PASSES} passes`,
          );
          set(synapseUploadErrorAtom, {
            title: 'Upload Error',
            description:
              'Some audio failed to upload. Please check your connection and try recording again.',
          });
          return null;
        }
      }

      // Track preprocessing start
      const preprocessingStartTime = Date.now();
      trackPreprocessingStarted({
        ambientSessionId,
        opfsSessionId: opfsSessionId ?? undefined,
        chunkCount,
      });

      // Get total duration and mute segments
      const totalDurationMs = get(finalDurationMsAtom);
      const segments = get(muteSegmentsAtom);

      // Map segments, using totalDurationMs as fallback for any unclosed segments
      const completeMuteSegments = segments.map((s) => ({
        startMs: s.startMs,
        endMs: s.endMs ?? totalDurationMs ?? s.startMs, // Fallback to startMs if no duration
      }));

      // Complete and preprocess (remux + upload to nucleus bucket)
      const result = await completeAndPreprocess(
        ambientSessionId,
        {
          practiceId: config.practiceId,
          localTranscript: params.localTranscript,
          totalDurationMs: totalDurationMs ?? undefined,
          muteSegments:
            completeMuteSegments.length > 0 ? completeMuteSegments : undefined,
        },
        deps,
      );

      const preprocessingDurationMs = Date.now() - preprocessingStartTime;

      trackPreprocessingCompleted({
        ambientSessionId,
        opfsSessionId: opfsSessionId ?? undefined,
        chunkCount,
        durationMs: preprocessingDurationMs,
      });

      debug.log('Session completed:', {
        nucleusS3Key: result.nucleusS3Key,
        durationSeconds: result.durationSeconds,
      });

      trackSessionCompleted({
        ambientSessionId,
        opfsSessionId: opfsSessionId ?? undefined,
        nucleusS3Key: result.nucleusS3Key,
        totalDurationMs: Date.now() - startTime,
        chunkCount,
        preprocessingDurationMs,
      });

      // Clean up OPFS session now that preprocessing succeeded.
      // Without this, OPFS sessions leak until stale session cleanup runs.
      if (opfsSessionId) {
        if (swAvailable) {
          // SW path: also clears the SW's upload-status bookkeeping
          completeUploadSession(opfsSessionId).catch((err) => {
            debug.warn('Failed to clean up OPFS session via SW:', err);
          });
        } else {
          // Direct-upload path: no SW involved, just delete from OPFS
          opfs.deleteSession(opfsSessionId).catch((err) => {
            debug.warn('Failed to clean up OPFS session:', err);
          });
        }
      }

      return {
        ...result,
        startTime: completionStartTime,
      };
    } catch (error) {
      debug.error('Failed to complete session:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';

      // Determine which stage failed
      const stage = errorMessage.includes('preprocess')
        ? 'preprocessing'
        : 'completion';

      if (stage === 'preprocessing') {
        trackPreprocessingFailed({
          ambientSessionId,
          opfsSessionId: opfsSessionId ?? undefined,
          error: errorMessage,
        });
      }

      trackSessionCompletionFailed({
        ambientSessionId,
        opfsSessionId: opfsSessionId ?? undefined,
        error: errorMessage,
        stage,
      });

      set(synapseUploadErrorAtom, {
        title: 'Processing Error',
        description: errorMessage,
      });
      return null;
    } finally {
      set(synapseUploadLoadingAtom, false);
    }
  },
);

/**
 * Clear ambient session state (after successful completion or cancellation).
 *
 * Note: This intentionally does NOT clear muteSegmentsAtom because:
 * 1. startRecordingAtom already resets mute segments when starting a new recording
 * 2. Clearing here would cause a race condition if a new recording starts while
 *    the previous upload is still completing - the new recording's mute segments
 *    would be lost when this clears them
 */
export const clearAmbientSessionAtom = atom(null, (get, set) => {
  debug.log('Clearing ambient session state');
  get(eventTrackerAtom).clearSessionId();
  set(ambientSessionIdAtom, null);
  set(registeredSessionPairAtom, null);
  set(pendingSessionCreationAtom, null);
  set(sessionCreationInProgressForAtom, null);
  set(synapseUploadErrorAtom, null);
  set(uploadStartTimeAtom, 0);
  set(pendingChunkWritePromiseAtom, Promise.resolve());
  set(uploadWorkerAvailableAtom, null);
});

/**
 * Retry session creation in background.
 * Called by effect atom when pendingSessionCreation is set.
 */
export const retrySessionCreationAtom = atom(
  null,
  async (
    get,
    set,
    params: {
      sessionParams: CreateSessionParams;
    },
  ): Promise<boolean> => {
    const pending = get(pendingSessionCreationAtom);
    const deps = get(ambientDepsAtom);
    const config = get(synapseUploadConfigAtom);

    if (!pending || !deps || !config) {
      return false;
    }

    const MAX_RETRIES = 60; // 5 minutes at 5 second intervals
    if (pending.retryCount >= MAX_RETRIES) {
      debug.warn('Max retries reached for session creation');
      set(pendingSessionCreationAtom, null);
      set(synapseUploadErrorAtom, {
        title: 'Session Creation Failed',
        description:
          'Unable to create recording session after multiple attempts',
      });
      return false;
    }

    debug.log('Retrying session creation, attempt:', pending.retryCount + 1);

    const opfsSessionId = get(recordingSessionIdAtom);

    const swAvailable = get(uploadWorkerAvailableAtom) !== false;

    try {
      const token = await deps.getSynapseToken();
      if (swAvailable) {
        await cacheTokenInServiceWorker(token);
      }

      const session = await createSession(
        {
          ...params.sessionParams,
          patientConsentRecordId: pending.consentRecordId,
        },
        deps,
      );

      set(ambientSessionIdAtom, session.id);
      set(pendingSessionCreationAtom, null);
      set(uploadStartTimeAtom, Date.now());

      // Register with SW (only when the upload SW is reachable)
      if (opfsSessionId && swAvailable) {
        await registerUploadSession(
          opfsSessionId,
          session.id,
          config.organizationId,
          config.practiceId,
          config.synapseApiUrl,
        );
        set(registeredSessionPairAtom, `${opfsSessionId}:${session.id}`);

        await triggerQueueProcessing();
      }

      // Persist to OPFS for recovery — non-fatal, done regardless of SW
      if (opfsSessionId) {
        try {
          await opfs.updateAmbientSessionId(opfsSessionId, session.id);
        } catch (opfsError) {
          debug.warn(
            'Failed to persist ambientSessionId to OPFS during retry:',
            opfsError,
          );
        }
      }

      debug.log('Retry succeeded, session:', session.id);
      return true;
    } catch (error) {
      debug.warn('Retry failed:', error);
      set(pendingSessionCreationAtom, {
        ...pending,
        retryCount: pending.retryCount + 1,
      });
      return false;
    }
  },
);

/**
 * Get elapsed time since upload started (for metrics).
 */
export const getUploadElapsedTimeAtom = atom((get) => {
  const startTime = get(uploadStartTimeAtom);
  if (startTime === 0) return 0;
  return Date.now() - startTime;
});
